import type {
	InsightsLiveUserSettingsType,
	LiveConfigurationsType,
	LiveProfileType,
	UserSettings,
	UserSettingsApplictionType,
} from "src/models/types/userSettings.types";
import { APP_CONFIG } from "./appConfig.constants";
import { DATA_TYPE, MEASUREMENT_UNIT } from "./live.constants";

// Utility type that makes all properties required recursively
type DeepRequired<T> = {
	[P in keyof T]-?: T[P] extends object ? DeepRequired<T[P]> : T[P];
};

export const LIVE_VIEW_MODE_TYPE = {
	CARDS: "cards",
	TABLE: "table",
} as const;

export const LIVE_STATE_MODE_TYPE = {
	ALL: "all",
	ACTIVE: "active",
	ALARMS: "alarms",
} as const;

export const LIVE_SORT_MODE_TYPE = {
	NAME: "name",
	BIOMASS: "biomass",
	ACTIVITY: "activity",
	FEEDING: "feeding",
} as const;

export const LIVE_CARD_VIEW_MODE_TYPE = {
	LINE: "line",
	BAR: "bar",
	CAKE: "cake",
} as const;

export const DEFAULT_LIVE_PROFILE_ID = "default";

export const DEFAULT_LIVE_PROFILE = {
	id: DEFAULT_LIVE_PROFILE_ID,
	name: "Default Profile",
	description: "Default profile for the live view",
	sites: {
		overviewCards: [
			{
				dataType: DATA_TYPE.water_oxygen_percentage,
				measurementUnit: MEASUREMENT_UNIT["%"],
				minDepth: 5,
				maxDepth: 10,
				usesDepth: true,
				aggregationType: "average",
			},
			{
				dataType: DATA_TYPE.water_salinity,
				measurementUnit: MEASUREMENT_UNIT.psu,
				minDepth: 5,
				maxDepth: 10,
				usesDepth: true,
				aggregationType: "average",
			},
			{
				dataType: DATA_TYPE.water_temperature,
				measurementUnit: MEASUREMENT_UNIT["°C"],
				minDepth: 5,
				maxDepth: 10,
				usesDepth: true,
				aggregationType: "average",
			},
		],
		locationCards: [
			{
				dataType: DATA_TYPE.water_oxygen_percentage,
				measurementUnit: MEASUREMENT_UNIT["%"],
				minDepth: 5,
				maxDepth: 10,
				usesDepth: true,
				aggregationType: "average",
			},
			{
				dataType: DATA_TYPE.water_salinity,
				measurementUnit: MEASUREMENT_UNIT.psu,
				minDepth: 5,
				maxDepth: 10,
				usesDepth: true,
				aggregationType: "average",
			},
			{
				dataType: DATA_TYPE.water_temperature,
				measurementUnit: MEASUREMENT_UNIT["°C"],
				minDepth: 5,
				maxDepth: 10,
				usesDepth: true,
				aggregationType: "average",
			},
		],
		showTrends: false,
		feed: {
			cardsViewMode: LIVE_CARD_VIEW_MODE_TYPE.LINE,
			intensityEnabled: true,
			amountEnabled: true,
			targetEnabled: true,
		},
	},
	units: {
		overviewCards: [
			{
				dataType: DATA_TYPE.water_oxygen_percentage,
				measurementUnit: MEASUREMENT_UNIT["%"],
				minDepth: 5,
				maxDepth: 10,
			},
			{
				dataType: DATA_TYPE.water_salinity,
				measurementUnit: MEASUREMENT_UNIT.psu,
				minDepth: 5,
				maxDepth: 10,
			},
			{
				dataType: DATA_TYPE.water_temperature,
				measurementUnit: MEASUREMENT_UNIT["°C"],
				minDepth: 5,
				maxDepth: 10,
			},
		],
		locationCards: [
			{
				dataType: DATA_TYPE.water_oxygen_percentage,
				measurementUnit: MEASUREMENT_UNIT["%"],
				minDepth: 5,
				maxDepth: 10,
			},
			{
				dataType: DATA_TYPE.water_salinity,
				measurementUnit: MEASUREMENT_UNIT.psu,
				minDepth: 5,
				maxDepth: 10,
			},
			{
				dataType: DATA_TYPE.water_temperature,
				measurementUnit: MEASUREMENT_UNIT["°C"],
				minDepth: 5,
				maxDepth: 10,
			},
		],
		showTrends: false,
		feed: {
			cardsViewMode: LIVE_CARD_VIEW_MODE_TYPE.LINE,
			intensityEnabled: true,
			amountEnabled: true,
			targetEnabled: true,
		},
	},
	filter: {
		selectedSiteId: undefined,
		lastUpdated: undefined,
		selectedLocations: [],
		selectedProfileId: "default",
		selectedStateMode: LIVE_STATE_MODE_TYPE.ALL,
		selectedSortMode: LIVE_SORT_MODE_TYPE.NAME,
		selectedViewMode: LIVE_VIEW_MODE_TYPE.CARDS,
	},
} as LiveProfileType;

export const DEFAULT_LIVE_CONFIGURATIONS = {
	profiles: [DEFAULT_LIVE_PROFILE],
	views: [DEFAULT_LIVE_PROFILE],
} as LiveConfigurationsType;

export const DEFAULT_USER_SETTINGS = {
	common: {
		language: "en",
		uiTheme: "light",
	},
	applicationSpecific: {
		insightsLive: {
			theme: "light",
			language: "en",
			decimalCount: 3,
			features: APP_CONFIG.features,
			selectedCompany: undefined,
			liveConfigurations: DEFAULT_LIVE_CONFIGURATIONS,
		} as InsightsLiveUserSettingsType,
	} as UserSettingsApplictionType,
} satisfies DeepRequired<UserSettings>;
